import { Controller } from '@hotwired/stimulus';

/**
 * Nested Form Controller
 *
 * Manages dynamic nested form fields for Rails `accepts_nested_attributes_for` associations.
 * Allows users to add and remove associated records without page reloads.
 *
 * ## How It Works
 * - Uses a `<template>` element containing the HTML for new rows
 * - Replaces `NEW_RECORD` placeholder with a unique timestamp for proper Rails param naming
 * - For existing records, marks them for destruction via `_destroy` field (Rails convention)
 * - For new records, simply removes them from the DOM
 *
 * ## Targets
 * - container: Element where new rows are appended (e.g., `<tbody>`)
 * - template: `<template>` element containing the HTML for new rows
 * - row: Each repeatable row element (used by remove action to find parent)
 * - destroyField: Hidden `_destroy` input field within each row
 *
 * ## Actions
 * - add: Creates a new row from the template and appends to container
 * - remove: Marks existing records for destruction or removes new records from DOM
 *
 * @example
 * ```slim
 * .card data-controller="nested-form"
 *   .card-header
 *     button type="button" data-action="click->nested-form#add"
 *       | Add Item
 *   .card-body
 *     table
 *       tbody data-nested-form-target="container"
 *         - @parent.children.each do |child|
 *           tr data-nested-form-target="row"
 *             td
 *               = f.fields_for :children, child do |cf|
 *                 = cf.hidden_field :id
 *                 = cf.hidden_field :_destroy, data: { nested_form_target: "destroyField" }
 *                 = cf.input :name
 *             td
 *               button type="button" data-action="click->nested-form#remove"
 *                 | Remove
 *
 *     template data-nested-form-target="template"
 *       tr data-nested-form-target="row"
 *         = f.fields_for :children, Child.new, child_index: "NEW_RECORD" do |cf|
 *           td
 *             = cf.hidden_field :_destroy, data: { nested_form_target: "destroyField" }
 *             = cf.input :name
 *           td
 *             button type="button" data-action="click->nested-form#remove"
 *               | Remove
 * ```
 */
export default class extends Controller {
  static targets = ['container', 'template', 'row', 'destroyField'];

  add(event) {
    event.preventDefault();
    const content = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, new Date().getTime());
    this.containerTarget.insertAdjacentHTML('beforeend', content);
  }

  remove(event) {
    event.preventDefault();
    const row = event.target.closest('[data-nested-form-target="row"]');
    if (!row) return;

    const destroyField = row.querySelector('[data-nested-form-target="destroyField"]');
    if (destroyField) {
      // Existing record - mark for destruction and hide
      destroyField.value = '1';
      row.style.display = 'none';
    } else {
      // New record - just remove from DOM
      row.remove();
    }
  }
}
